import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthShell from '../components/AuthShell';
import { Button } from '../components/ui/button';
import { apiService } from '../services/api';

// Mirror of the backend's redirect_uri validation (sdk/views_auth.py): the SDK
// may only send the code to a loopback callback, or "oob" for headless logins.
const REDIRECT_URI_RE = /^http:\/\/(127\.0\.0\.1|localhost):\d{2,5}\/callback$/;
const OOB = 'oob';

/**
 * SdkAuthPage — consent screen for the qombra Python SDK browser login.
 *
 * Opened by `qombra.login()` with PKCE params in the query string. A logged-in
 * user approves; we mint a single-use auth code via /api/sdk/auth/approve/ and
 * hand it back to the SDK's 127.0.0.1 callback (or display it for copy-paste
 * in headless "oob" mode). Not logged in → round-trip through /login?next=…
 */
const SdkAuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const state = params.get('state') || '';
  const codeChallenge = params.get('code_challenge') || '';
  const redirectUri = params.get('redirect_uri') || '';
  const clientName = params.get('client') || 'qombra Python SDK';

  const [email, setEmail] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [issuedCode, setIssuedCode] = useState('');
  const [copied, setCopied] = useState(false);

  const isOob = redirectUri === OOB;
  const paramsValid =
    /^[A-Za-z0-9_-]{43}$/.test(codeChallenge) &&
    (isOob || (REDIRECT_URI_RE.test(redirectUri) && state));

  useEffect(() => {
    apiService
      .getAccount()
      .then((res) => setEmail(res.data?.user?.email || res.data?.email || ''))
      .catch(() => {
        const next = location.pathname + location.search;
        navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApprove = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await apiService.sdkApprove({
        code_challenge: codeChallenge,
        redirect_uri: redirectUri,
        client_name: clientName,
      });
      const code = res.data.code;
      setIssuedCode(code);
      if (!isOob) {
        window.location.assign(
          `${redirectUri}?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`
        );
      }
    } catch (err) {
      setError(err?.response?.data?.error?.message || 'Authorization failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleDeny = () => {
    if (!isOob) {
      window.location.assign(`${redirectUri}?error=access_denied&state=${encodeURIComponent(state)}`);
    } else {
      navigate('/app');
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(issuedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (email === null) {
    return (
      <AuthShell>
        <p className="auth-subheading">Checking your session…</p>
      </AuthShell>
    );
  }

  if (!paramsValid) {
    return (
      <AuthShell>
        <h1 className="auth-heading">
          SDK <span className="auth-heading__accent">login</span>
        </h1>
        <p className="auth-error">
          This authorization link is invalid or incomplete. Please re-run{' '}
          <code>qombra.login()</code> and use the link it opens.
        </p>
      </AuthShell>
    );
  }

  if (issuedCode) {
    return (
      <AuthShell>
        <h1 className="auth-heading">
          Access <span className="auth-heading__accent">granted</span>
        </h1>
        {isOob ? (
          <>
            <p className="auth-subheading">
              Paste this one-time code into your terminal. It expires in 5 minutes.
            </p>
            <div className="auth-field">
              <input className="auth-input" readOnly value={issuedCode} onFocus={(e) => e.target.select()} />
            </div>
            <div className="auth-submit">
              <Button className="auth-btn-block" onClick={handleCopy}>
                {copied ? 'Copied!' : 'Copy code'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="auth-subheading">
              Handing the login back to your Python session… You can close this window.
            </p>
            <p className="auth-subheading">
              If your terminal did not receive it, copy the code manually:
            </p>
            <div className="auth-field">
              <input className="auth-input" readOnly value={issuedCode} onFocus={(e) => e.target.select()} />
            </div>
            <div className="auth-submit">
              <Button variant="outline" className="auth-btn-block" onClick={handleCopy}>
                {copied ? 'Copied!' : 'Copy code'}
              </Button>
            </div>
          </>
        )}
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="auth-heading">
        Authorize <span className="auth-heading__accent">SDK access</span>
      </h1>
      <p className="auth-subheading">
        <strong>{clientName}</strong> is requesting API access to your Qombra account
        {email ? ` (${email})` : ''}. The session token is valid for 12 hours and can
        be revoked at any time by closing the SDK session.
      </p>

      {error && <p className="auth-error">{error}</p>}

      <div className="auth-submit">
        <Button className="auth-btn-block" onClick={handleApprove} disabled={busy}>
          {busy ? 'Authorizing…' : 'Approve'}
        </Button>
      </div>
      <div className="auth-submit">
        <Button variant="outline" className="auth-btn-block" onClick={handleDeny} disabled={busy}>
          Deny
        </Button>
      </div>
    </AuthShell>
  );
};

export default SdkAuthPage;
